// Consent banner (consumer app) — docs/replan-v2.md R8, stubbed per §37.1.
//
// Mirrors the marketing-site banner: functional is always on; analytics and
// marketing are opt-in toggles. The decision lands in the fa_consent cookie +
// the API's append-only consent_records. Nothing third-party loads in this app
// regardless of the choice (kid surfaces live here — CLAUDE.md §29); the
// marketing consent recorded here governs future parent-surface ad tooling.
//
// Hidden for kid sessions: a child can't give consent (PDPL), and the banner
// is noise on the kid home. The parent account holder decides.

import type { ConsentChoices } from '@fajrarabi/analytics';
import { useT } from '@fajrarabi/i18n/react';
import { useState } from 'react';

import { readConsent, saveConsent } from '~/lib/analytics';
import { useAuth } from '~/lib/auth';
import { shellForContext } from '~/lib/roles';

export function ConsentBanner() {
  const t = useT();
  const { currentRoleContext } = useAuth();
  const [decided, setDecided] = useState(() => readConsent() !== null);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  if (decided) return null;
  if (shellForContext(currentRoleContext) === 'kid') return null;

  const decide = (choices: ConsentChoices) => {
    saveConsent(choices);
    setDecided(true);
  };

  return (
    <section aria-labelledby="consent-title" className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4">
      <div className="mx-auto max-w-3xl rounded-2xl border border-cream-200 bg-cream-50 p-4 shadow-[0_8px_40px_rgba(31,26,23,0.18)] sm:p-5">
        <h2 id="consent-title" className="text-sm font-bold text-ink-900">
          {t('app.consent.title')}
        </h2>
        <p className="mt-1.5 text-sm leading-relaxed text-ink-700">{t('app.consent.body')}</p>

        {expanded ? (
          <div className="mt-3 space-y-3">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-burgundy-500"
              />
              <span className="text-sm">
                <span className="font-semibold text-ink-900">
                  {t('app.consent.analytics_label')}
                </span>
                <span className="block text-ink-500">{t('app.consent.analytics_desc')}</span>
              </span>
            </label>
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-burgundy-500"
              />
              <span className="text-sm">
                <span className="font-semibold text-ink-900">
                  {t('app.consent.marketing_label')}
                </span>
                <span className="block text-ink-500">{t('app.consent.marketing_desc')}</span>
              </span>
            </label>
            <button
              type="button"
              onClick={() => decide({ functional: true, analytics, marketing })}
              className="rounded-lg border border-burgundy-500 px-4 py-2 text-sm font-semibold text-burgundy-500 transition hover:bg-burgundy-50"
            >
              {t('app.consent.save')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="mt-2 text-sm font-semibold text-burgundy-500 underline-offset-2 hover:underline"
          >
            {t('app.consent.customize')}
          </button>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => decide({ functional: true, analytics: true, marketing: true })}
            className="rounded-lg bg-burgundy-500 px-4 py-2 text-sm font-semibold text-cream-50 transition hover:bg-burgundy-600"
          >
            {t('app.consent.accept_all')}
          </button>
          <button
            type="button"
            onClick={() => decide({ functional: true, analytics: false, marketing: false })}
            className="rounded-lg border border-cream-200 bg-cream-100 px-4 py-2 text-sm font-semibold text-ink-700 transition hover:bg-cream-200"
          >
            {t('app.consent.essential_only')}
          </button>
        </div>
      </div>
    </section>
  );
}
